
import { Component, Vue } from "vue-property-decorator";
import { DimssaButton } from "@/components/shared/dimssa-button.vue";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { formatDetailBreadcrumbLabel, setupDetailBreadcrumbs } from "@/utils/breadcrumbs";

interface BlendSessionMix {
  session: number;
  rationAGuid?: string;
  rationBGuid?: string;
  rationAPct?: number;
  rationBPct?: number;
}

interface BlendPlanDay {
  day: number;
  sessions: BlendSessionMix[];
}

interface BlendPlan {
  typename?: string;
  guid?: string;
  name: string;
  days?: number;
  dayPlans: BlendPlanDay[];
  creationDate?: number;
}

@Component({
  components: {
    DimssaButton,
    BreadcrumbTrail,
  },
})
export default class BlendPlanView extends Vue {
  loading = false;
  changed = false;
  blendPlan: BlendPlan = { typename: "BlendPlan", guid: "", name: "", dayPlans: [] };
  rations: any[] = [];
  kraalTypes: any[] = [];
  feedingStrategyItems: any[] = [];
  sessionCount = 0;
  selectedRationA: string | null = null;
  selectedRationB: string | null = null;
  selectedKraalTypeGuid: string | null = null;
  dayCount = 0;

  get breadcrumbs() {
    const detailLabel = formatDetailBreadcrumbLabel("Blend Plan", this.blendPlan?.name);
    return setupDetailBreadcrumbs({
      listText: "Blend Plans",
      listRoute: "/blendPlans",
      currentText: detailLabel,
    });
  }

  mounted() {
    this.loadPlanFromStore();
    this.fetchRations();
    this.fetchStrategy();
    this.fetchKraalTypes();
  }

  loadPlanFromStore() {
    const stored = (this.$store.getters as any)["storage"]?.();
    if (stored && stored.blendPlan) {
      this.blendPlan = { ...stored.blendPlan };
      this.dayCount = this.blendPlan.dayPlans?.length || this.blendPlan.days || 0;
    } else {
      this.blendPlan.guid = `${Date.now()}`;
      this.dayCount = 1;
    }
    this.syncDayPlans();
  }

  get sessionDefaultCount() {
    const storedCount = this.blendPlan.dayPlans?.reduce((max, dp) => Math.max(max, dp.sessions?.length || 0), 0) || 0;
    const strategyCount = this.feedingStrategyItems.length || 0;

    if (this.selectedKraalTypeGuid) {
      const kt = this.kraalTypes.find((k: any) => k.guid === this.selectedKraalTypeGuid);
      if (kt && kt.noOfSessions && kt.noOfSessions > 0) {
        return kt.noOfSessions;
      }
    }

    return Math.max(storedCount, strategyCount, 1);
  }

  getChoice(dayIndex: number, sessionNumber: number): "A" | "B" | null {
    const session = this.getSession(dayIndex, sessionNumber);
    if (!session) return null;
    if (session.rationAPct && session.rationAPct > 0) return "A";
    if (session.rationBPct && session.rationBPct > 0) return "B";
    return null;
  }

  getSession(dayIndex: number, sessionNumber: number): BlendSessionMix | undefined {
    return this.blendPlan.dayPlans[dayIndex]?.sessions?.find((s) => s.session === sessionNumber);
  }

  setChoice(dayIndex: number, sessionNumber: number, choice: "A" | "B") {
    this.changed = true;
    const day = this.blendPlan.dayPlans[dayIndex];
    if (!day) return;
    const session = this.getSession(dayIndex, sessionNumber);
    const rationAGuid = this.selectedRationA || session?.rationAGuid;
    const rationBGuid = this.selectedRationB || session?.rationBGuid;
    const rationAPct = choice === "A" ? 100 : 0;
    const rationBPct = choice === "B" ? 100 : 0;
    if (session) {
      session.rationAPct = rationAPct;
      session.rationBPct = rationBPct;
      session.rationAGuid = rationAGuid || undefined;
      session.rationBGuid = rationBGuid || undefined;
    }
  }

  getSessionLabel(dayIndex: number, sessionNumber: number) {
    const choice = this.getChoice(dayIndex, sessionNumber);
    if (!choice) return "Unassigned";
    const guid = choice === "A" ? this.selectedRationA : this.selectedRationB;
    return choice === "A" ? `Ration A (${this.resolveRationName(guid)})` : `Ration B (${this.resolveRationName(guid)})`;
  }

  resolveRationName(guid?: string | null) {
    if (!guid) return "";
    const ration = this.rations.find((r: any) => r.guid === guid);
    return ration ? ration.name : "";
  }

  syncDayPlans() {
    const count = Math.max(1, this.dayCount || 1);
    if (!Array.isArray(this.blendPlan.dayPlans)) this.blendPlan.dayPlans = [];
    while (this.blendPlan.dayPlans.length < count) {
      const nextIndex = this.blendPlan.dayPlans.length;
      this.blendPlan.dayPlans.push({ day: nextIndex + 1, sessions: [] });
    }
    this.blendPlan.dayPlans = this.blendPlan.dayPlans.slice(0, count).map((dp, idx) => ({ ...dp, day: idx + 1 }));
    this.syncSessions();
  }

  syncSessions() {
    this.sessionCount = Math.max(1, this.sessionDefaultCount || 1);
    this.blendPlan.dayPlans.forEach((day) => {
      if (!Array.isArray(day.sessions)) day.sessions = [];
      while (day.sessions.length < this.sessionCount) {
        const sessionNum = day.sessions.length + 1;
        day.sessions.push({
          session: sessionNum,
          rationAGuid: this.selectedRationA || undefined,
          rationBGuid: this.selectedRationB || undefined,
          rationAPct: 0,
          rationBPct: 0,
        });
      }
      day.sessions = day.sessions.slice(0, this.sessionCount).map((s, idx) => ({ ...s, session: idx + 1 }));
    });
  }

  onDayCountChange() {
    if (this.dayCount < 1) this.dayCount = 1;
    this.syncDayPlans();
  }

  onKraalTypeChange() {
    this.changed = true;
    this.syncSessions();
  }

  onRationChange() {
    this.changed = true;
    this.syncSessions();
  }

  async fetchRations() {
    try {
      const json = await this.$store.dispatch("graphQl", {
        gql: `query($guid:String!){ Rations(guid:$guid){ guid name } }`,
        variables: { guid: this.$store.state.user.location.guid },
      });
      this.rations = json.data.Rations ?? [];
      if (!this.selectedRationA && this.rations.length > 0) this.selectedRationA = this.rations[0].guid;
      if (!this.selectedRationB && this.rations.length > 1) this.selectedRationB = this.rations[1].guid;
      this.syncSessions();
    } catch (err) {
      console.log("fetchRations error", err);
    }
  }

  async fetchStrategy() {
    try {
      const json = await this.$store.dispatch("graphQl", {
        gql: `query($guid:String!){ Strategy(guid:$guid){ FeedingPlanItems{ sequence } } }`,
        variables: { guid: this.$store.state.user.location.guid },
      });
      this.feedingStrategyItems = json.data.Strategy?.FeedingPlanItems ?? [];
      this.syncSessions();
    } catch (err) {
      console.log("fetchStrategy error", err);
    }
  }

  async fetchKraalTypes() {
    try {
      const json = await this.$store.dispatch("graphQl", {
        gql: `query($guid:String!){ KraalType(guid:$guid){ guid kraalType noOfSessions } }`,
        variables: { guid: this.$store.state.user.location.guid },
      });
      this.kraalTypes = json.data.KraalType ?? [];
    } catch (err) {
      console.log("fetchKraalTypes error", err);
    }
  }

  normalizeBlendPlan(): BlendPlan {
    const payload: BlendPlan = {
      ...this.blendPlan,
      guid: this.blendPlan.guid || `${Date.now()}`,
      typename: "BlendPlan",
      name: this.blendPlan.name,
      days: this.blendPlan.dayPlans.length,
      dayPlans: this.blendPlan.dayPlans.map((dp, dIdx) => ({
        day: dIdx + 1,
        sessions: dp.sessions.slice(0, this.sessionCount).map((s, sIdx) => {
          const choice = this.getChoice(dIdx, sIdx + 1);
          const rationAGuid = this.selectedRationA || s.rationAGuid;
          const rationBGuid = this.selectedRationB || s.rationBGuid;
          return {
            session: sIdx + 1,
            rationAGuid,
            rationBGuid,
            rationAPct: choice === "A" ? 100 : 0,
            rationBPct: choice === "B" ? 100 : 0,
          };
        }),
      })),
    };
    return payload;
  }

  async save() {
    const payload = this.normalizeBlendPlan();
    try {
      await this.$store.dispatch("graphQl", {
        gql: `mutation blendPlan($guid:String!, $blendPlan: BlendPlanInput!){
          blendPlan(guid:$guid, blendPlan:$blendPlan)
        }`,
        variables: {
          guid: this.$store.state.user.location.guid,
          blendPlan: payload,
        },
      });
      this.changed = false;
      this.$router.go(-1);
    } catch (err) {
      console.log("save blend plan error", err);
    }
  }

  cancel() {
    if (this.changed) {
      this.$store.commit("popup/displayYesNo", {
        message: "Discard changes?",
        yesAction: () => this.$router.go(-1),
      });
    } else {
      this.$router.go(-1);
    }
  }
}
